import { Button, Input, List, Modal, Space, Spin } from 'antd'
import { ReactNode, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faLink } from '@fortawesome/pro-solid-svg-icons'
import { blue } from '@ant-design/colors'

type QboEntity = {
  id: string
  displayName: string
}

type Props<T extends QboEntity> = {
  open: boolean
  onCancel: () => void
  title: string
  searchPlaceholder: string
  items: T[] | undefined
  isLoading: boolean
  initialSearchFilter?: string
  selectedItemId: string | undefined
  onSelectItem: (item: T) => void
  renderItemLabel: (item: T) => ReactNode
  headerContent?: ReactNode
  footerExtra: ReactNode
  onOpen?: () => void
}

const QboEntityPickerModal = <T extends QboEntity>({
  open,
  onCancel,
  title,
  searchPlaceholder,
  items,
  isLoading,
  initialSearchFilter,
  selectedItemId,
  onSelectItem,
  renderItemLabel,
  headerContent,
  footerExtra,
  onOpen,
}: Props<T>) => {
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter ?? '')

  useEffect(() => {
    if (open) {
      setSearchFilter(initialSearchFilter ?? '')
      onOpen?.()
    }
  }, [open])

  const filteredItems = (items ?? []).filter((item) =>
    item.displayName.toLowerCase().includes(searchFilter.toLowerCase())
  )

  return (
    <Modal
      title={
        <Space>
          <FontAwesomeIcon icon={faLink} /> {title}
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={
        <>
          <Button onClick={onCancel} icon={<FontAwesomeIcon icon={faArrowLeft} />}>
            Close
          </Button>
          {footerExtra}
        </>
      }
      width={520}
    >
      {headerContent}

      <Input
        value={searchFilter}
        style={{ marginBottom: 16 }}
        placeholder={searchPlaceholder}
        onChange={(e) => setSearchFilter(e.target.value)}
      />

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin /> <span style={{ marginLeft: 8 }}>Loading QuickBooks items...</span>
        </div>
      ) : (
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          <List
            itemLayout="horizontal"
            dataSource={filteredItems}
            renderItem={(item) => (
              <List.Item
                onClick={() => onSelectItem(item)}
                style={{
                  cursor: 'pointer',
                  background: item.id === selectedItemId ? blue[1] : 'transparent',
                  padding: 16,
                }}
              >
                {renderItemLabel(item)}
              </List.Item>
            )}
          />
        </div>
      )}
    </Modal>
  )
}

export default QboEntityPickerModal
